import { useState, useCallback, useRef, useEffect } from 'react';

interface UseVisionCaptureOptions {
    onFrame?: (base64Data: string) => void;
    fps?: number;
    quality?: number;
    maxWidth?: number;
    maxHeight?: number;
    mode?: 'continuous' | 'on-demand';
}

/**
 * Hook to capture screen/window frames and process them for Gemini Vision.
 * Uses getDisplayMedia for capture and a hidden canvas for downscaling.
 */
export function useVisionCapture(options: UseVisionCaptureOptions = {}) {
    const {
        onFrame,
        fps = 1,
        quality = 0.6,
        maxWidth = 768,
        maxHeight = 768,
        mode = 'continuous'
    } = options;

    const [isActive, setIsActive] = useState(false);
    const isActiveRef = useRef(false);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const streamRef = useRef<MediaStream | null>(null);

    // Refs for processing - we use refs to avoid re-triggering the capture loop
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const intervalRef = useRef<number | null>(null);
    const onFrameRef = useRef(onFrame);



    // Keep callback ref fresh
    useEffect(() => {
        onFrameRef.current = onFrame;
    }, [onFrame]);

    const captureFrame = useCallback(() => {
        if (!videoRef.current || !canvasRef.current || !isActiveRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        // Safety check - ensures video is playing and has content
        if (video.readyState < 2 || video.paused || video.ended) {
            // Try to force play if paused
            if (video.paused && !video.ended && video.srcObject) {
                video.play().catch(e => console.error('[Vision] Force play failed', e));
            }
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Calculate dimensions maintaining aspect ratio
        let width = video.videoWidth;
        let height = video.videoHeight;

        if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
        }

        // Update canvas size if needed (expensive if done every frame, but we're only at 1 FPS)
        if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
        }

        // Draw and encode
        ctx.drawImage(video, 0, 0, width, height);

        // Use JPEG for optimization
        const base64 = canvas.toDataURL('image/jpeg', quality);
        const data = base64.split(',')[1];

        if (data) {
            onFrameRef.current?.(data);
        }
    }, [maxWidth, maxHeight, quality]);

    // Expose a manual capture function for on-demand mode
    const captureOneFrame = useCallback(() => {
        captureFrame();
    }, [captureFrame]);

    const stopCapture = useCallback(() => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
            setStream(null);
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setIsActive(false);
        isActiveRef.current = false;
    }, []);

    const startCapture = useCallback(async (sourceId?: string) => {
        let displayStream: MediaStream | undefined;
        try {
            if (sourceId) {
                // Electron capture using specific source
                displayStream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: {
                        // @ts-expect-error - Electron specific constraints
                        mandatory: {
                            chromeMediaSource: 'desktop',
                            chromeMediaSourceId: sourceId,
                            maxWidth: 1920,
                            maxHeight: 1080
                        }
                    }
                });
            } else {
                // Standard browser capture with picker
                displayStream = await navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface: 'monitor',
                        // Prefer higher resolution for the stream, we downscale locally
                        width: { ideal: 1920 },
                        height: { ideal: 1080 }
                    },
                    audio: false
                });
            }

            // Handle user stopping share via browser UI buttons (BUG 1: optional chaining, BUG 2: stop tracks directly)
            const videoTrack = displayStream.getVideoTracks()[0];
            if (videoTrack) {
                const stream = displayStream;
                videoTrack.onended = () => {
                    stream.getTracks().forEach(t => t.stop());
                    stopCapture();
                };
            }

            // Set up hidden video element
            if (!videoRef.current) {
                const video = document.createElement('video');
                video.autoplay = true;
                video.playsInline = true;
                video.muted = true;
                videoRef.current = video;
            }
            videoRef.current.srcObject = displayStream;
            videoRef.current.play().catch(e => console.error('[Vision] Failed to play hidden video', e));

            // Hidden canvas for downscaling
            if (!canvasRef.current) {
                canvasRef.current = document.createElement('canvas');
            }

            setStream(displayStream);
            streamRef.current = displayStream;
            setIsActive(true);
            isActiveRef.current = true;

            // Start capture loop
            // Note: We use useEffect for the interval now to avoid cleanup race conditions
        } catch (error) {
            console.error('[VisionCapture] Failed to start:', error);
            if (displayStream) {
                displayStream.getTracks().forEach(t => t.stop());
            }
            setIsActive(false);
            setStream(null);
        }
    }, [stopCapture]);

    // Manage capture interval declaratively (continuous mode only)
    useEffect(() => {
        if (mode === 'continuous' && isActive && stream) {
            const intervalMs = 1000 / fps;
            const id = window.setInterval(captureFrame, intervalMs);
            intervalRef.current = id;

            return () => {
                window.clearInterval(id);
                intervalRef.current = null;
            };
        }
    }, [mode, isActive, stream, fps, captureFrame]);

    // Clean up stream on unmount
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    return {
        isActive,
        startCapture,
        stopCapture,
        captureOneFrame,
    };
}
